<script setup>
import { computed } from 'vue'
import { startCase } from 'lodash'

const props = defineProps({
    filters: {
        type: Object,
        required: true,
    },
    source: {
        type: Object,
        default: null,
    },
    filterLabels: {
        type: Object,
        default: () => ({}),
    },
})

const conjunctionLabels = {
    '$and': 'All',
    '$or': 'Any',
    '$nin': 'Any',
    '$notall': 'All',
}

const getLabel = (path) => props.filterLabels[path] || props.filterLabels[path.split('.').slice(0, -1).join('.')] || startCase(path.split('.').pop())

const getDisplayValue = (value) => {
    if (value && typeof value === 'object') {
        if (value.label) return value.label
        if (value.name) return value.name
        if (value.title) return value.title
        if (value.id) return value.id
    }

    if (typeof value === 'string' && value.toLowerCase() === 'twitter') {
        return 'X'
    }

    return value
}

const hasDisplayValue = (value) => {
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value === true
    if (typeof value === 'number') return true
    if (typeof value === 'string') return value.length > 0
    if (!value || typeof value !== 'object') return false

    if (value.label || value.name || value.title || value.id) return true
    if (value.min !== null && value.min !== undefined) return true
    if (value.max !== null && value.max !== undefined) return true

    return Object.entries(value).some(([key, nestedValue]) => key !== 'conjunction' && hasDisplayValue(nestedValue))
}

const formatValues = (values) => values.map(getDisplayValue).filter(Boolean).join(', ')

const percentRangeFilters = new Set(['avg_engagement_rate'])

const formatRangeNumber = (path, value) => {
    const displayValue = percentRangeFilters.has(path) ? value * 100 : value

    return displayValue.toLocaleString(undefined, {
        maximumFractionDigits: 2,
    })
}

const formatRange = (path, value) => {
    if (value.min !== null && value.min !== undefined && value.max !== null && value.max !== undefined) {
        return `${formatRangeNumber(path, value.min)} to ${formatRangeNumber(path, value.max)}`
    }

    if (value.min !== null && value.min !== undefined) {
        return `at least ${formatRangeNumber(path, value.min)}`
    }

    if (value.max !== null && value.max !== undefined) {
        return `up to ${formatRangeNumber(path, value.max)}`
    }

    return null
}

const createItem = (label, value) => (
    value !== null && value !== undefined && value !== ''
        ? [{ label, value }]
        : []
)

const getSourceDisplay = () => {
    if (!props.source?.type || props.source.type === 'platform' || !hasDisplayValue(props.source?.input)) return null

    if (props.source.type === 'mixed') {
        if (props.source.name) return props.source.name

        const listIds = Array.isArray(props.source.input.list_id) ? props.source.input.list_id : (props.source.input.list_id ? [props.source.input.list_id] : [])
        const campaignIds = Array.isArray(props.source.input.campaign_id) ? props.source.input.campaign_id : (props.source.input.campaign_id ? [props.source.input.campaign_id] : [])
        const sourceParts = []

        if (listIds.length) {
            sourceParts.push(`Lists: ${listIds.length === 1 ? listIds[0] : `${listIds.length} lists selected`}`)
        }

        if (campaignIds.length) {
            sourceParts.push(`Campaigns: ${campaignIds.length === 1 ? campaignIds[0] : `${campaignIds.length} campaigns selected`}`)
        }

        return sourceParts.join('; ')
    }

    const pluralLabels = {
        list: 'Lists',
        campaign: 'Campaigns',
    }
    const sourceType = Array.isArray(props.source.input) && props.source.input.length > 1
        ? pluralLabels[props.source.type] || startCase(props.source.type)
        : startCase(props.source.type)

    if (props.source.name) {
        return `${sourceType}: ${props.source.name}`
    }

    const sourceInput = Array.isArray(props.source.input) ? props.source.input.join(', ') : getDisplayValue(props.source.input)
    return `${sourceType}: ${sourceInput}`
}

const getFilterItems = (path, value, parent = null) => {
    if (path === 'source' || path.endsWith('.conjunction') || path.endsWith('.index') || !hasDisplayValue(value)) {
        return []
    }

    const label = getLabel(path)

    if (Array.isArray(value)) {
        const conjunction = parent?.conjunction ? ` (${conjunctionLabels[parent.conjunction] || parent.conjunction})` : ''
        return createItem(label, `${formatValues(value)}${conjunction}`)
    }

    if (typeof value === 'boolean') {
        return createItem(label, 'Yes')
    }

    if (typeof value !== 'object' || value === null) {
        return createItem(label, getDisplayValue(value))
    }

    if (value.label || value.name || value.title || value.id) {
        return createItem(label, getDisplayValue(value))
    }

    const range = formatRange(path, value)
    if (range) {
        return createItem(label, range)
    }

    return Object.entries(value).flatMap(([key, nestedValue]) => getFilterItems(`${path}.${key}`, nestedValue, value))
}

const appliedFilters = computed(() => {
    const sourceItem = createItem('Source', getSourceDisplay())

    return [
        ...sourceItem,
        ...Object.entries(props.filters || {}).flatMap(([key, value]) => getFilterItems(key, value)),
    ]
})
</script>

<template>
    <div>
        <div class="pb-1 pt-2 text-sm font-semibold">Applied Filters</div>
        <div v-if="appliedFilters.length" class="flex flex-wrap gap-1.5">
            <span
                v-for="filter in appliedFilters"
                :key="`${filter.label}-${filter.value}`"
                class="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700"
            >
                <span class="mr-1 font-medium">{{ filter.label }}:</span>
                <span>{{ filter.value }}</span>
            </span>
        </div>
        <span v-else class="text-xs italic text-slate-500">Default filters</span>
    </div>
</template>
